import { ArrowLeft, Sparkles, Users, BookOpen, Timer, Flame, Tag, Workflow, Type, Palette, Box, LayoutGrid } from 'lucide-react';
import { useCMS } from '../context/CMSContext';
import { useState, useEffect } from 'react';
import { collection, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';

const WHATSAPP_MSG = encodeURIComponent('  اريد حجز مقعدي في عرض ال 48 ساعة');

export default function Hero() {
  const { content, loading } = useCMS();
  const [targetCount, setTargetCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  // ─── Countdown Timer – driven by Firestore offerStartTime + offerDurationHours ───
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!content.offerStartTime) return;
    const durationMs = (Number(content.offerDurationHours) || 48) * 60 * 60 * 1000;
    const startMs = new Date(content.offerStartTime).getTime();

    const calc = () => {
      const diff = durationMs - (Date.now() - startMs);
      return diff > 0 ? diff : 0;
    };
    setTimeLeft(calc());

    const id = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(id);
  }, [content.offerStartTime, content.offerDurationHours]);

  const hours = Math.floor(timeLeft / 3600000);
  const minutes = Math.floor((timeLeft % 3600000) / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);

  // ─── Student Count ───
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const coll = collection(db, 'registrations');
        const snapshot = await getCountFromServer(coll);
        setTargetCount(snapshot.data().count);
        setIsLoaded(true);
      } catch (error) {
        console.error("Error fetching count:", error);
        setTargetCount(0);
        setIsLoaded(true);
      }
    };
    fetchCount();
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    let startTime;
    const duration = 2500;
    let animationFrame;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 4);
      setDisplayCount(Math.floor(easeOut * targetCount));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setDisplayCount(targetCount);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [targetCount, isLoaded]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return num.toString();
  };

  // ─── Dynamic Pricing ───
  const oldPrice = Number(content.offerOldPrice) || 65;
  const newPrice = Number(content.offerNewPrice) || 50;
  const saved = oldPrice - newPrice;
  const discountPct = Math.round((saved / oldPrice) * 100);

  const showOffer = content.offerEnabled !== false;

  return (
    <section id="hero" className="relative overflow-hidden pt-36 lg:pt-48 pb-24 lg:pb-32 bg-slate-50 dark:bg-zinc-950 transition-colors duration-500 min-h-[100vh] flex flex-col" dir="rtl">
      <style>{`
        @keyframes float-badge {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-12px) rotate(2deg); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(224,0,0,0.2), 0 0 40px rgba(224,0,0,0.08); }
          50% { box-shadow: 0 0 25px rgba(224,0,0,0.35), 0 0 60px rgba(224,0,0,0.15); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes countdown-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Background */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03] dark:opacity-10 mix-blend-overlay"></div>
      <div className="absolute top-0 right-0 -mr-40 -mt-40 bg-[#e00000] rounded-full w-[600px] h-[600px] blur-[100px] dark:blur-[150px] opacity-10 dark:opacity-20 pointer-events-none transition-all duration-500"></div>
      <div className="absolute bottom-0 left-0 -ml-40 -mb-40 bg-[#b80000] rounded-full w-[500px] h-[500px] blur-[120px] dark:blur-[150px] opacity-10 dark:opacity-15 pointer-events-none transition-all duration-500"></div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000008_1px,transparent_1px),linear-gradient(to_bottom,#00000008_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none transition-colors duration-500"></div>

      <div className="container mx-auto px-4 relative z-10 grid lg:grid-cols-2 gap-16 lg:gap-8 items-center my-auto">
        {/* Right side: Content */}
        <div className="max-w-3xl order-1">
          <div className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-primary-600 dark:text-primary-400 text-xl font-bold mb-8 backdrop-blur-md shadow-sm dark:shadow-[0_0_15px_rgba(224,0,0,0.1)] transition-all duration-500">
            <Sparkles className="w-4 h-4" />
            <span>{content.heroBadge}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-[1.2] tracking-tight text-slate-900 dark:text-white drop-shadow-xl dark:drop-shadow-2xl transition-colors duration-500">
            {content.heroTitle} <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-l from-primary-600 via-primary-500 to-primary-700 dark:from-primary-400 dark:via-primary-500 dark:to-primary-600 drop-shadow-[0_0_10px_rgba(224,0,0,0.2)] dark:drop-shadow-[0_0_15px_rgba(224,0,0,0.4)] transition-all duration-500">{content.heroHighlight}</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-600 dark:text-zinc-400 mb-12 leading-relaxed font-medium max-w-2xl transition-colors duration-500">
            {content.heroSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-5">
            <a href="#register" className="group flex items-center justify-center gap-3 bg-primary-600 hover:bg-primary-500 text-white px-10 py-5 rounded-2xl font-extrabold text-xl transition-all duration-300 shadow-[0_0_20px_rgba(224,0,0,0.3)] hover:shadow-[0_0_35px_rgba(224,0,0,0.5)] hover:-translate-y-1 ring-1 ring-primary-500/50">
              <span>{content.heroButtonText}</span>
              <ArrowLeft className="w-6 h-6 group-hover:-translate-x-2 transition-transform" />
            </a>
            <a href="#roadmap" className="flex items-center justify-center px-10 py-5 rounded-2xl font-bold text-xl transition-all duration-300 bg-white/70 dark:bg-transparent border border-slate-300 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5 text-slate-700 dark:text-zinc-300 backdrop-blur-md dark:hover:border-white/20 shadow-sm dark:shadow-none">
              {content.heroSecondaryButtonText}
            </a>
            <Link to="/blog" className="flex items-center justify-center gap-3 px-8 py-5 rounded-2xl font-bold text-xl transition-all duration-300 bg-amber-500 hover:bg-amber-400 text-slate-900 shadow-xl hover:-translate-y-1">
              <BookOpen className="w-6 h-6" />
              <span>تصفح المدونة</span>
            </Link>
          </div>
        </div>

        {/* ═══ Left side: OFFER CARD (conditional) ═══ */}
        <div className="relative order-2 mt-8 lg:mt-0" style={{ animation: 'slide-in 0.8s ease-out' }}>

          {/* Floating Students Badge */}
          <div
            className="absolute -top-6 lg:-top-12 -right-4 lg:-right-8 z-50 bg-white/80 dark:bg-zinc-900/60 backdrop-blur-2xl border border-white/50 dark:border-white/10 rounded-2xl p-3 md:p-4 flex items-center gap-3 shadow-[0_15px_40px_rgba(0,0,0,0.1)] dark:shadow-2xl hover:scale-105 transition-transform duration-300"
            style={{ animation: 'float-badge 6s ease-in-out infinite' }}
          >
            <div className="bg-primary-600 rounded-xl w-11 h-11 md:w-12 md:h-12 flex items-center justify-center shadow-lg shadow-primary-600/40 flex-shrink-0">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl font-extrabold text-slate-900 dark:text-white leading-none mb-0.5 text-left" dir="ltr">
                {formatNumber(displayCount)}
              </span>
              <span className="text-md md:text-mdfont-bold text-slate-500 dark:text-zinc-400">
                طلاب مسجلين
              </span>
            </div>
          </div>

          {showOffer ? (
            /* ══════════════════════════════════════════════════
               PREMIUM OFFER CARD — Dark Glassmorphism Design
            ══════════════════════════════════════════════════ */
            <div className="relative group/offer" style={{ animation: 'pulse-glow 4s ease-in-out infinite' }}>

              {/* Discount badge — floating top-left */}
              {saved > 0 && (
                <div className="absolute -top-3 -left-3 z-30 bg-green-500 text-white text-mdfont-black px-4 py-2 rounded-full shadow-lg shadow-green-500/30 rotate-[-8deg]">
                  خصم {discountPct}%
                </div>
              )}

              {/* Main Card */}
              <div className="relative rounded-[1.5rem] overflow-hidden bg-gradient-to-b from-zinc-900 via-zinc-900 to-zinc-950 border border-white/[0.08] shadow-[0_25px_80px_rgba(0,0,0,0.4)]">

                {/* ── Course Images — Side by Side ── */}
                <div className="relative grid grid-cols-2 gap-3 p-4 md:p-5 bg-gradient-to-b from-zinc-800/30 to-zinc-900/60">

                  {/* Course 1 */}
                  <div className="relative rounded-xl overflow-hidden border border-white/10 group/c1 hover:border-primary-500/40 transition-all duration-500">
                    <div className="aspect-[3/4]">
                      {content.offerCourse1Image ? (
                        <img src={content.offerCourse1Image} alt={content.offerCourse1Name} className="w-full h-full object-cover group-hover/c1:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center">
                          <Sparkles className="w-10 h-10 text-zinc-600" />
                        </div>
                      )}
                    </div>
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3 pt-10">
                      <p className="text-white text-mdfont-bold truncate">{content.offerCourse1Name}</p>
                      <span className="text-primary-400 text-mdfont-black">${content.offerCourse1Price}</span>
                    </div>
                  </div>

                  {/* "+" connector */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center shadow-lg shadow-primary-600/40 text-white font-black text-lg ring-[3px] ring-zinc-900">
                    +
                  </div>

                  {/* Course 2 */}
                  <div className="relative rounded-xl overflow-hidden border border-white/10 group/c2 hover:border-primary-500/40 transition-all duration-500">
                    <div className="aspect-[3/4]">
                      {content.offerCourse2Image ? (
                        <img src={content.offerCourse2Image} alt={content.offerCourse2Name} className="w-full h-full object-cover group-hover/c2:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center">
                          <Sparkles className="w-10 h-10 text-zinc-600" />
                        </div>
                      )}
                    </div>
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3 pt-10">
                      <p className="text-white text-mdfont-bold truncate">{content.offerCourse2Name}</p>
                      <span className="text-primary-400 text-mdfont-black">${content.offerCourse2Price}</span>
                    </div>
                  </div>
                </div>

                {/* ── Card Body ── */}
                <div className="p-5 md:p-7 space-y-5">

                  {/* Headline + ribbon inline */}
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-xl md:text-2xl font-extrabold text-white leading-tight">
                        دورتين بسعر واحد
                      </h3>
                      <p className="text-mdtext-zinc-400 font-medium mt-0.5">عرض لفترة محدودة</p>
                    </div>
                    <div className="relative bg-gradient-to-l from-primary-600 to-primary-700 text-white text-md font-black px-4 py-2 rounded-xl overflow-hidden flex-shrink-0">
                      <div className="absolute inset-0 bg-[linear-gradient(110deg,transparent_30%,rgba(255,255,255,0.15)_45%,transparent_60%)] bg-[length:200%_100%]" style={{ animation: 'shimmer 3s infinite linear' }}></div>
                      <span className="relative z-10 flex items-center gap-1.5"><Flame className="w-4 h-4" /> {content.offerDurationHours || 48}h</span>
                    </div>
                  </div>

                  {/* Countdown — horizontal with colon separators */}
                  <div className="flex items-center justify-center gap-0.5 md:gap-1" dir="ltr">
                    <Timer className="w-4 h-4 md:w-5 md:h-5 text-primary-500 ml-1 md:ml-2" />
                    {[
                      { val: String(hours).padStart(2, '0'), label: 'ساعة' },
                      { val: String(minutes).padStart(2, '0'), label: 'دقيقة' },
                      { val: String(seconds).padStart(2, '0'), label: 'ثانية' },
                    ].map((t, i) => (
                      <div key={i} className="flex items-center gap-0.5 md:gap-1">
                        <div className="flex flex-col items-center">
                          <span className="bg-white/[0.07] text-white text-lg md:text-3xl font-black w-10 md:w-14 py-1.5 md:py-2 rounded-lg md:rounded-xl text-center tabular-nums border border-white/[0.06]">
                            {t.val}
                          </span>
                          <span className="text-[8px] md:text-[10px] mt-0.5 md:mt-1 font-bold text-zinc-500">{t.label}</span>
                        </div>
                        {i < 2 && <span className="text-primary-500 font-black text-lg md:text-2xl mb-4 md:mb-5 mx-0.5 md:mx-1">:</span>}
                      </div>
                    ))}
                  </div>

                  {/* Pricing Row */}
                  <div className="flex items-center justify-between bg-white/[0.04] rounded-xl px-5 py-4 border border-white/[0.06]">
                    <div className="flex items-center gap-3">
                      <span className="line-through text-zinc-500 font-bold text-lg decoration-primary-500/60 decoration-2">${oldPrice}</span>
                      <ArrowLeft className="w-5 h-5 text-zinc-600" />
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-400 text-mdfont-bold">الآن فقط</span>
                      <span className="bg-gradient-to-l from-primary-600 to-primary-500 text-white font-black text-3xl md:text-4xl px-5 py-1.5 rounded-xl shadow-lg shadow-primary-600/25">${newPrice}</span>
                    </div>
                  </div>

                  {/* WhatsApp CTA */}
                  <a
                    href={`https://wa.me/${content.offerWhatsappNumber || ''}?text=${WHATSAPP_MSG}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group/cta flex items-center justify-center gap-3 w-full py-5 rounded-2xl font-black text-xl text-white bg-[#25D366] hover:bg-[#20c05c] shadow-[0_10px_35px_rgba(37,211,102,0.3)] hover:shadow-[0_15px_45px_rgba(37,211,102,0.45)] transition-all duration-300 hover:-translate-y-1"
                  >
                    <svg className="w-7 h-7 group-hover/cta:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                    <span>الاشتراك الآن – ${newPrice}</span>
                    <ArrowLeft className="w-6 h-6 group-hover/cta:-translate-x-2 transition-transform" />
                  </a>

                </div>
              </div>
            </div>
          ) : (
            /* ── Design & Branding Visual Grid (when offer is hidden) ── */
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 transform lg:rotate-y-[-12deg] lg:rotate-x-[8deg] transition-transform duration-700 lg:hover:rotate-y-0 lg:hover:rotate-x-0 group">

              {/* Card 1: Logo Construction Lines */}
              <div className="col-span-2 row-span-2 bg-white/70 dark:bg-zinc-900/40 backdrop-blur-2xl border border-slate-200 dark:border-white/10 rounded-lg p-8 flex flex-col items-center justify-center gap-6 relative overflow-hidden group/card shadow-xl hover:border-primary-500/50 transition-all duration-500">
                <div className="absolute inset-0 bg-primary-500 opacity-0 group-hover/card:opacity-[0.03] transition-opacity duration-500"></div>
                <div className="relative w-36 h-36 flex items-center justify-center">
                  <Workflow className="w-32 h-32 text-primary-600 dark:text-primary-500/50 group-hover/card:scale-110 group-hover/card:text-primary-500 transition-all duration-700" strokeWidth={1} />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-opacity duration-500">
                    <div className="w-36 h-36 border border-primary-500/30 rounded-full animate-ping"></div>
                    <div className="absolute w-20 h-20 border border-primary-500/40 rounded-full animate-pulse"></div>
                  </div>
                </div>
                <p className="text-base font-extrabold text-slate-400 dark:text-zinc-500 uppercase tracking-widest transition-colors group-hover/card:text-primary-500">Logo Engineering</p>
              </div>

              {/* Card 2: Color Palette */}
              <div className="bg-white/70 dark:bg-zinc-900/40 backdrop-blur-2xl border border-slate-200 dark:border-white/10 rounded-lg p-5 flex flex-col items-center justify-center gap-3 group/card hover:border-primary-500/50 transition-all duration-500 shadow-xl overflow-hidden relative">
                <div className="flex -space-x-3 rtl:space-x-reverse h-14 items-center">
                  <div className="w-9 h-9 rounded-full bg-[#e00000] shadow-[0_0_15px_rgba(224,0,0,0.4)] z-30 group-hover/card:scale-110 transition-transform"></div>
                  <div className="w-9 h-9 rounded-full bg-zinc-700 z-20 group-hover/card:scale-110 transition-transform delay-75"></div>
                  <div className="w-9 h-9 rounded-full bg-zinc-400 z-10 group-hover/card:scale-110 transition-transform delay-150"></div>
                </div>
                <Palette className="w-5 h-5 text-slate-400 dark:text-zinc-500 group-hover/card:text-primary-500 transition-colors" />
              </div>

              {/* Card 3: Typography */}
              <div className="bg-white/70 dark:bg-zinc-900/40 backdrop-blur-2xl border border-slate-200 dark:border-white/10 rounded-lg p-5 flex flex-col items-center justify-center gap-3 group/card hover:border-primary-500/50 transition-all duration-500 shadow-xl">
                <div className="flex gap-3 items-center">
                  <span className="text-3xl font-extrabold text-slate-900 dark:text-white group-hover/card:text-primary-500 transition-colors">ع</span>
                  <div className="h-7 w-[1px] bg-slate-200 dark:bg-white/10"></div>
                  <span className="text-3xl font-extrabold text-slate-900 dark:text-white opacity-50">ع</span>
                </div>
                <Type className="w-5 h-5 text-slate-400 dark:text-zinc-500 group-hover/card:text-primary-500 transition-colors" />
              </div>

              {/* Card 4: Golden Ratio */}
              <div className="bg-white/70 dark:bg-zinc-900/40 backdrop-blur-2xl border border-slate-200 dark:border-white/10 rounded-lg p-5 flex flex-col items-center justify-center gap-3 group/card hover:border-primary-500/50 transition-all duration-500 shadow-xl relative overflow-hidden">
                <div className="w-full h-16 relative flex items-center justify-center">
                  <svg className="w-14 h-14 text-slate-400 dark:text-zinc-600 group-hover/card:text-primary-500/50 transition-all duration-500" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="0.5">
                    <circle cx="50" cy="50" r="40" />
                    <rect x="10" y="10" width="80" height="80" />
                    <line x1="10" y1="10" x2="90" y2="90" />
                    <line x1="90" y1="10" x2="10" y2="90" />
                  </svg>
                </div>
                <LayoutGrid className="w-5 h-5 text-slate-400 dark:text-zinc-500 group-hover/card:text-primary-500 transition-colors" />
              </div>

              {/* Card 5: Geometric Shapes */}
              <div className="bg-white/70 dark:bg-zinc-900/40 backdrop-blur-2xl border border-slate-200 dark:border-white/10 rounded-lg p-5 flex flex-col items-center justify-center gap-3 group/card hover:border-primary-500/50 transition-all duration-500 shadow-xl relative overflow-hidden">
                <div className="flex -space-x-2 rtl:space-x-reverse relative h-10 items-center">
                  <div className="w-9 h-9 rounded-full border-2 border-primary-500/30 group-hover/card:border-primary-500 transition-colors"></div>
                  <div className="w-9 h-9 border-2 border-slate-300 dark:border-white/20 rotate-45 group-hover/card:rotate-[225deg] transition-all duration-700"></div>
                </div>
                <Box className="w-5 h-5 text-slate-400 dark:text-zinc-500 group-hover/card:text-primary-500 transition-colors" />
              </div>

            </div>
          )}
        </div>
      </div>
    </section>
  );
}
